import { enumNarrow } from '../helpers/enumGuard'
import { IBorrowingBaseIndex, IMasterInvoice, IOngoingReporting } from './bbc'
import { IClientInfo } from './client'
import { IMasterCollection } from './collection'
import { IEntityInfo } from './entityInfo'
import { IFee } from './loanServicing'
import { INoteMapping } from './notes'
import { IParticipant } from './participant'
import { IUser } from './user'

export enum SearchType {
  Client = 'Client',
  Entity = 'Entity',
  Funding = 'Borrowing Base',
  Prospect = 'Prospect',
  DueDiligence = 'Due Diligence',
  Collection = 'Collection',
  Fee = 'Fee',
  Note = 'Note',
  User = 'User',
  OngoingReporting = 'Ongoing Reporting',
  Participant = 'Participant',
  Invoice = 'Invoice',
  Organization = 'Organization',
  Company = 'Company',
  Person = 'Person',
  Task = 'Task',
  Sku = 'SKU',
}

// CRM search documents. These augment/replace the legacy Entity/Client/User
// results: a legacy record with a CRM counterpart (linked via the id fields
// below) is suppressed from its legacy bucket and shown here instead.
export interface ISearchOrganization {
  id: string
  name: string | null
  type: string | null
  subType: string | null
  roles: string[]
  ownerName: string | null
  entityInfoId: string | null
  lastInteractionDate: string | Date | null
}

export interface ISearchCompany {
  id: string
  name: string | null
  status: string | null
  industryType: string | null
  industrySubtype: string | null
  ownerName: string | null
  clientInfoId: string | null
  clientName: string | null
  clientStatus: string | null
  commitment: number | null
  lastInteractionDate: string | Date | null
  // Client-lifecycle fields (populated when the company is linked to a client_info).
  // Drive lifecycle-aware routing in global search and BDO prospect scoping.
  firstOPSReportingId: string | null
  hasProspectInfo: boolean
  iconUrl: string | null
  // True when linked to a client_info — used to rank clients above non-client
  // (vendor/customer) companies within the Company bucket.
  isClient: boolean
}

export interface ISearchPerson {
  id: string
  firstName: string | null
  lastName: string | null
  fullName: string
  emailAddress: string | null
  phoneNumber: string | null
  mobilePhone: string | null
  primaryTitle: string | null
  primaryAffiliation: string | null
  type: string
  ownerName: string | null
  userId: string | null
  lastInteractionDate: string | Date | null
}

// Tasks are searched across every user (not scoped to the searcher), with the
// searcher's own tasks boosted — see buildTaskQuery in services/search/Search.ts.
export interface ISearchTask {
  id: string
  title: string
  descriptionText: string | null
  status: string | null
  assignedToUserId: string | null
  assigneeName: string | null
  createdByUserId: string | null
  clientName: string | null
  dueDate: string | Date | null
  // Precomputed from CLOSED_TASK_STATUSES at index time so the query can boost
  // active tasks without restating the closed-status list in OpenSearch terms.
  isCompleted: boolean
  parentTaskId: string | null
}

// One document per (client, SKU) in a current client's live master inventory.
export interface ISearchSku {
  id: string
  sku: string
  description: string | null
  clientName: string
  clientId: string
  lastAppearedDate: string | null
}

/** Cast-free narrower for SearchType (see common/helpers/enumGuard). */
export const asSearchType = enumNarrow(SearchType).as

export const GLOBAL_SEARCH_TYPE_TITLES = {
  [SearchType.Client]: 'Clients',
  [SearchType.DueDiligence]: 'Due Diligence',
  [SearchType.Prospect]: 'Prospects',
  [SearchType.Entity]: 'Entities',
  [SearchType.Funding]: 'Borrowing Bases',
  [SearchType.Collection]: 'Collections',
  [SearchType.Fee]: 'Fees',
  [SearchType.Invoice]: 'Invoices',
  [SearchType.Note]: 'Notes',
  [SearchType.User]: 'Users',
  [SearchType.OngoingReporting]: 'Ongoing Reporting',
  [SearchType.Participant]: 'Participants',
  [SearchType.Organization]: 'Organizations',
  [SearchType.Company]: 'Companies',
  [SearchType.Person]: 'People',
  [SearchType.Task]: 'Tasks',
  [SearchType.Sku]: 'SKUs',
}

export interface ISearchResultItem {
  id: string
  title: string
  type: SearchType
  link: string
  status: string
  item:
    | IClientInfo
    | IBorrowingBaseIndex
    | IMasterCollection
    | IFee
    | INoteMapping
    | IOngoingReporting
    | IUser
    | IEntityInfo
    | IParticipant
    | IMasterInvoice
    | ISearchOrganization
    | ISearchCompany
    | ISearchPerson
    | ISearchTask
    | ISearchSku
}

export interface ISearchResult {
  type: SearchType
  total: number
  maxScore: number
  items: ISearchResultItem[]
}

export const SEARCH_LIMIT = 3
